import { useMemo } from 'react';
import useAllDeviceItems from './useAllDeviceItems';

/**
 * Returns the set of pet GraphQL IDs for pets the current rescue has fully
 * adopted out (COMPLETED_ADOPTIONS). Callers cross-reference this set against
 * chip rows to gate mutation actions on adopted pets.
 *
 * The Devices page no longer uses this: `Pet.rescueAdoptionState` answers the
 * same question per row, so a device row reads its own state rather than a
 * separately drained list. The Chips page cannot follow yet —
 * `ResellerChip.pet` is a `StrangerPet`, which carries no adoption state, and
 * no field on the chip reaches a `Pet`. Moving this one needs the state
 * exposed on the reseller chip type, which is backend work.
 *
 * Scope: backed by `rescueDevices`, which only enumerates pets that ever had
 * a GPS device. Chip-only adoptions (no device ever attached) won't appear
 * here — those flows rely on the chip's own `assignedTo === 'adopter'` signal
 * to gate the destructive Clear Owner action. `rescueAdoptionState` does not
 * have that blind spot, which is another reason the Chips page should move.
 *
 * `loading` is true until the first page has resolved. Consumers should treat
 * loading as "adoption status unknown" and avoid showing irreversible mutation
 * actions for rows that have a pet attached until it flips to false.
 */
export default function useAdoptedPetIds(): { adoptedPetIds: Set<string>; loading: boolean } {
  const { items, loading } = useAllDeviceItems('COMPLETED_ADOPTIONS');
  const adoptedPetIds = useMemo(() => new Set(items.map((item) => item.pet.id)), [items]);
  return { adoptedPetIds, loading };
}
